import { Badge, type BadgeProps } from '@chakra-ui/react'
import { useMemo } from 'react'
import {
  type AutoColorTheme,
  getAutoColorBadgeColors,
} from '~/utils/colorUtils'

export interface AutoColorBadgeProps extends Omit<BadgeProps, 'children'> {
  /** The text label to display in the badge */
  label: string
  /** Theme mode - affects color contrast for accessibility */
  theme: AutoColorTheme
  /** Optional key to use for color generation instead of label (useful when label contains prefixes/translations) */
  colorKey?: string
}

/**
 * A badge component that generates a deterministic background color
 * based on the label text. Colors are WCAG accessible for the given theme.
 *
 * Uses Chakra's Badge component under the covers for consistency.
 *
 * @example
 * ```tsx
 * <AutoColorBadge label="Active" theme="light" />
 * <AutoColorBadge label="Pending" theme="dark" />
 * <AutoColorBadge label="Current: Staging" colorKey="staging" theme="light" />
 * ```
 */
export const AutoColorBadge = ({
  label,
  theme,
  colorKey,
  style,
  ...props
}: AutoColorBadgeProps) => {
  const colors = useMemo(() => {
    try {
      return getAutoColorBadgeColors(colorKey || label, theme)
    } catch {
      // Fallback colors if label is invalid
      return {
        background: theme === 'light' ? '#6b7280' : '#d1d5db',
        text: theme === 'light' ? '#ffffff' : '#1a1a1a',
      }
    }
  }, [colorKey, label, theme])

  return (
    <Badge
      variant="solid"
      borderRadius="full"
      px={2.5}
      py={0.5}
      fontSize="xs"
      fontWeight="medium"
      textTransform="none"
      style={{
        backgroundColor: colors.background,
        color: colors.text,
        transition: 'all 0.2s ease',
        ...style,
      }}
      {...props}
    >
      {label}
    </Badge>
  )
}
